import * as React from 'react';
import { Alert } from 'lib/alert/models';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { NotificationsOff } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggleAlertMute } from 'lib/alert/hooks/useToggleAlertMute';
import { MANTY_COLORS } from 'business/constants';
import { useDispatch } from 'store/hooks';
import { HttpError } from 'store/APIClient';
import CircularProgress from '@mui/material/CircularProgress';
import { useQueryClient } from '@tanstack/react-query';
import TooltipIconButton from '../TooltipIconButton';

type Props = {
    alert: Alert;
    chartKey: number;
};

const MuteAlertButton = ({ alert, chartKey }: Props): JSX.Element => {
    const [isMuted, setIsMuted] = useState(alert.isMuted);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const onSuccess = (message: string) => {
        queryClient.setQueryData(
            ['chart', chartKey, 'alerts', 'subscribed'],
            (oldAlerts: Array<Alert>) =>
                oldAlerts.map((oldAlert) => {
                    if (oldAlert.id === alert.id) {
                        return {
                            ...oldAlert,
                            isMuted: !oldAlert.isMuted,
                        };
                    }
                    return oldAlert;
                }),
        );
        dispatch.vision.notifyUser({
            message,
            snackType: 'success',
        });
        setIsMuted((prevState) => !prevState);
    };

    const onError = (error: HttpError) => {
        dispatch.vision.notifyUser({
            message: error.message,
            snackType: 'error',
        });
    };

    const { mutate: toggleMuted, isPending } = useToggleAlertMute(
        alert.id,
        onSuccess,
        onError,
    );

    const getMuteIcon = (): JSX.Element => {
        if (isPending) {
            return (
                <CircularProgress
                    size={24}
                    sx={{ color: MANTY_COLORS.darkGrey }}
                />
            );
        }
        if (isMuted) {
            return <NotificationsIcon sx={{ color: MANTY_COLORS.darkGrey }} />;
        }
        return <NotificationsOff sx={{ color: MANTY_COLORS.darkGrey }} />;
    };

    const toggleAlertMuted = () => toggleMuted(!isMuted);

    const muteTooltip = isMuted
        ? t('alert.muted.disable')
        : t('alert.muted.enable');

    return (
        <TooltipIconButton
            onClick={toggleAlertMuted}
            icon={getMuteIcon()}
            isDisabled={isPending}
            tooltip={muteTooltip}
        />
    );
};

export default MuteAlertButton;
